import React from "react";
import styled from "styled-components";
import { intersection, isEmpty } from "lodash";
import { TeamHeaderRow } from "source/team/components/TeamHeaderRow";
import { UserRow } from "source/team/components/UserRow";
import {
  Scroller,
  ScrollerContent,
} from "@sonder/ui/source/styled/fragments/Scroller";
import { Table } from "@sonder/ui/source/styled/fragments/Table";
import { sizes } from "@sonder/ui/source/styled/theme";
import { useStore as useAuthStore } from "auth/store";
import {
  UserData,
  mapSquadToTagName,
  useGetTeamQuery,
} from "core/queries/GetTeam";
import { EmptyRow, TaskTitle } from "tasks/components/TaskList";
import { useStore as useTeamStore } from "team/store";
import {
  SortAscDesc,
  UserFilterInput,
  UserSortColumn,
} from "typings/globalGQLTypes";

const StyledScroller = styled(Scroller)`
  border-radius: 0 0 0.3rem 0.3rem;
`;

const TeamListOuterContainer = styled.div`
  box-sizing: border-box;
  max-width: ${sizes.listWidth};
  margin: 0 auto;
  width: 100%;
  padding: 1rem 1rem 1rem 1.5rem;
`;

const filterUser = (user: UserData, globalFilters: UserFilterInput) => {
  const { roles: filteredRoles, squads: filteredSquads } = globalFilters;
  const { dispatchRoles, tags: userTags } = user;

  if (
    filteredRoles &&
    filteredRoles.length > 0 &&
    (!dispatchRoles ||
      intersection(
        filteredRoles,
        dispatchRoles.map(role => role.name),
      ).length === 0)
  )
    return false;

  if (
    filteredSquads &&
    filteredSquads.length > 0 &&
    (!userTags ||
      intersection(
        filteredSquads.map(squad => mapSquadToTagName(squad)),
        userTags.map(tag => tag.name),
      ).length === 0)
  )
    return false;

  return true;
};

export const TeamList = () => {
  const { state: authState } = useAuthStore();
  const { state: teamState } = useTeamStore();

  const cityId = authState.user?.cityId;
  const { data, error, loading } = useGetTeamQuery({
    skip: cityId === undefined,
    variables: {
      filter: {
        cityId,
      },
      sort: [
        {
          column: UserSortColumn.first_name,
          order: SortAscDesc.asc,
        },
      ],
    },
  });

  const users = (data?.userConnections.users || []).filter(user =>
    filterUser(user, teamState.filters),
  );
  const userCount = users.length;

  return (
    <TeamListOuterContainer>
      <TaskTitle count={userCount} isCollapsed={false} title="Team members" />
      <StyledScroller>
        <ScrollerContent>
          <Table columns="15rem minmax(15rem, 1fr) 15rem minmax(25rem, 1fr) 3.5rem">
            <TeamHeaderRow />
            {isEmpty(users) && (
              <EmptyRow
                errorLabel={error ? "Something went wrong" : undefined}
                label="No team members"
                loadingLabel={loading ? "Loading..." : undefined}
              />
            )}
            {users.map(({ id, ...rest }, index) => (
              <UserRow
                id={id}
                key={id}
                {...rest}
                lastRow={index === users.length - 1}
              />
            ))}
          </Table>
        </ScrollerContent>
      </StyledScroller>
    </TeamListOuterContainer>
  );
};
